import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PrintingBoardPage from './pages/PrintingBoardPage';
import JobsPage from './pages/JobsPage';
import PrintersPage from './pages/PrintersPage';
import StlPage from './pages/StlPage';
import FilamentPage from './pages/FilamentPage';
import NozzlePage from './pages/NozzlePage';
import AdminsPage from './pages/AdminsPage';
import TaskEditorPage from './pages/TaskEditorPage';
import KanbanPage from './pages/KanbanPage';
import KanbanCardPage from './pages/KanbanCardPage';
import PartnersPage from './pages/PartnersPage';
import EstimatesPage from './pages/EstimatesPage';
import EstimateWritePage from './pages/EstimateWritePage';
import ProductsPage from './pages/ProductsPage';
import CalendarPage from './pages/CalendarPage';
import AnalyticsPage from './pages/AnalyticsPage';
import QcPage from './pages/QcPage';
import MaterialsPage from './pages/MaterialsPage';
import FailuresPage from './pages/FailuresPage';
import MaintenancePage from './pages/MaintenancePage';
import CostingPage from './pages/CostingPage';
import EnergyPage from './pages/EnergyPage';
import ErrorBoundary from './components/ErrorBoundary';
import NoticesPage from './pages/NoticesPage';
import NoticeDetailPage from './pages/NoticeDetailPage';
import PostsPage from './pages/PostsPage';
import PostCollabPage from './pages/PostCollabPage';
import SchedulePage from './pages/SchedulePage';
import BoardsDeptPage from './pages/BoardsDeptPage';
import DailyWorkPage from './pages/DailyWorkPage';
import AccessLogPage from './pages/AccessLogPage';
import IdeaWorkshopPage from './pages/IdeaWorkshopPage';
import IdeaArchivePage from './pages/IdeaArchivePage';
import IdeaDetailPage from './pages/IdeaDetailPage';
import IdeaWritePage from './pages/IdeaWritePage';
import TripReportsPage from './pages/TripReportsPage';
import TripReportWritePage from './pages/TripReportWritePage';
import TripReportViewPage from './pages/TripReportViewPage';
import GoodsCalendarPage from './pages/GoodsCalendarPage';
import SalesCalendarPage from './pages/SalesCalendarPage';
import GoodsScheduleDetailPage from './pages/GoodsScheduleDetailPage';
import ResearchLogPage from './pages/ResearchLogPage';
import ResearchLogEditorPage from './pages/ResearchLogEditorPage';
import MeetingNotePage from './pages/MeetingNotePage';
import MeetingNoteEditorPage from './pages/MeetingNoteEditorPage';
import SparePartsPage from './pages/SparePartsPage';
import PlaudRecordingsPage from './pages/PlaudRecordingsPage';
import GmailPage from './pages/GmailPage';
import DrivePage from './pages/DrivePage';
import GoogleConnectPage from './pages/GoogleConnectPage';
import GoogleChatPage from './pages/GoogleChatPage';
import ChatPopupPage from './pages/ChatPopupPage';

function RequireAuth() {
  const token = localStorage.getItem('mfg_token');
  if (!token) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </Layout>
  );
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage onLogin={() => { window.location.href = '/notices'; }} />,
  },
  {
    element: <RequireAuth />,
    children: [
      // 기본 진입점 → 업무 관련
      { path: '/', element: <Navigate to="/notices" replace /> },
      // 생산 관련
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/printing-board', element: <PrintingBoardPage /> },
      { path: '/jobs', element: <JobsPage /> },
      { path: '/printers', element: <PrintersPage /> },
      { path: '/stl', element: <StlPage /> },
      { path: '/filament', element: <FilamentPage /> },
      { path: '/nozzles', element: <NozzlePage /> },
      { path: '/monitor', element: <PrintersPage /> },

      // 분석·품질
      { path: '/analytics', element: <AnalyticsPage /> },
      { path: '/qc', element: <QcPage /> },
      { path: '/materials', element: <MaterialsPage /> },
      { path: '/failures', element: <FailuresPage /> },
      { path: '/maintenance', element: <MaintenancePage /> },
      { path: '/costing', element: <CostingPage /> },
      { path: '/energy', element: <EnergyPage /> },

      // 업무 관련
      { path: '/notices', element: <NoticesPage /> },
      { path: '/notices/:id', element: <NoticeDetailPage /> },
      { path: '/schedule', element: <SchedulePage /> },
      { path: '/archive', element: <PostsPage category="archive" title="기록원" /> },
      { path: '/archive/:id', element: <PostCollabPage category="archive" title="기록원" /> },
      { path: '/trips', element: <TripReportsPage /> },
      { path: '/trips/new', element: <TripReportWritePage /> },
      { path: '/trips/:id', element: <TripReportViewPage /> },
      { path: '/trips/:id/edit', element: <TripReportWritePage /> },
      { path: '/news', element: <PostsPage category="news" title="뉴스 스크랩" /> },
      { path: '/news/:id', element: <PostCollabPage category="news" title="뉴스 스크랩" /> },
      { path: '/random', element: <PostsPage category="random" title="허무맹랑 저장소" /> },
      { path: '/random/:id', element: <PostCollabPage category="random" title="허무맹랑 저장소" /> },

      // 상품개발
      { path: '/product', element: <PostsPage category="product" title="상품개발" /> },
      { path: '/product/:id', element: <PostCollabPage category="product" title="상품개발" /> },

      // 상품개발본부
      { path: '/work/product/calendar', element: <CalendarPage /> },
      { path: '/work/product/goods-calendar', element: <GoodsCalendarPage department="product" basePath="/work/product" /> },
      { path: '/work/product/goods/:id', element: <GoodsScheduleDetailPage /> },
      { path: '/work/material/goods-calendar', element: <GoodsCalendarPage department="material" basePath="/work/material" /> },
      { path: '/work/material/goods/:id', element: <GoodsScheduleDetailPage /> },
      { path: '/work/equipment/goods-calendar', element: <GoodsCalendarPage department="equipment" basePath="/work/equipment" /> },
      { path: '/work/equipment/goods/:id', element: <GoodsScheduleDetailPage /> },
      { path: '/work/product/daily', element: <DailyWorkPage key="product-daily" department="product" /> },
      { path: '/work/product/research', element: <ResearchLogPage /> },
      { path: '/work/product/research/:id', element: <ResearchLogEditorPage /> },
      { path: '/work/meeting', element: <MeetingNotePage /> },
      { path: '/work/meeting/:id', element: <MeetingNoteEditorPage /> },
      { path: '/work/plaud', element: <PlaudRecordingsPage /> },
      { path: '/spare-parts', element: <SparePartsPage /> },
      { path: '/work/product/boards', element: <BoardsDeptPage key="product-boards" department="product" /> },
      { path: '/work/product/boards/:boardId/tasks/:taskId', element: <TaskEditorPage basePath="/work/product/boards" /> },

      // 소재개발본부
      { path: '/work/material/daily', element: <DailyWorkPage key="material-daily" department="material" /> },
      { path: '/work/material/boards', element: <BoardsDeptPage key="material-boards" department="material" /> },
      { path: '/work/material/boards/:boardId/tasks/:taskId', element: <TaskEditorPage basePath="/work/material/boards" /> },

      // 장비개발본부
      { path: '/work/equipment/daily', element: <DailyWorkPage key="equipment-daily" department="equipment" /> },
      { path: '/work/equipment/boards', element: <BoardsDeptPage key="equipment-boards" department="equipment" /> },
      { path: '/work/equipment/boards/:boardId/tasks/:taskId', element: <TaskEditorPage basePath="/work/equipment/boards" /> },

      // 기존 경로 호환 리다이렉트
      { path: '/boards', element: <Navigate to="/work/product/boards" replace /> },
      { path: '/boards/:boardId', element: <Navigate to="/work/product/boards" replace /> },
      { path: '/boards/:boardId/tasks/:taskId', element: <TaskEditorPage basePath="/work/product/boards" /> },
      { path: '/calendar', element: <Navigate to="/work/product/calendar" replace /> },

      // 아이디어 워크숍
      { path: '/idea-workshop', element: <IdeaWorkshopPage /> },
      { path: '/idea-archive', element: <IdeaArchivePage /> },
      { path: '/idea-archive/:id', element: <IdeaDetailPage /> },
      { path: '/idea-write', element: <IdeaWritePage /> },

      // 관리자
      { path: '/work/product/kanban',          element: <KanbanPage department="product" /> },
      { path: '/work/product/kanban/:cardId',  element: <KanbanCardPage department="product" /> },
      { path: '/work/material/kanban',         element: <KanbanPage department="material" /> },
      { path: '/work/material/kanban/:cardId', element: <KanbanCardPage department="material" /> },
      { path: '/work/equipment/kanban',        element: <KanbanPage department="equipment" /> },
      { path: '/work/equipment/kanban/:cardId',element: <KanbanCardPage department="equipment" /> },
      { path: '/partners', element: <PartnersPage /> },
      { path: '/estimates', element: <EstimatesPage /> },
      { path: '/estimates/write', element: <EstimateWritePage /> },
      { path: '/estimates/:id', element: <EstimateWritePage /> },
      { path: '/products', element: <ProductsPage /> },
      { path: '/work/product/sales', element: <SalesCalendarPage /> },
      { path: '/admins', element: <AdminsPage /> },
      { path: '/admins/access-logs', element: <AccessLogPage /> },
      { path: '/gmail', element: <GmailPage /> },
      { path: '/drive', element: <DrivePage /> },
      { path: '/google/connect', element: <GoogleConnectPage /> },
      { path: '/chat/:spaceId?', element: <GoogleChatPage /> },
    ],
  },
  { path: '/chat-popup/:spaceId', element: <ChatPopupPage /> },
  { path: '*', element: <Navigate to="/notices" replace /> },
]);

export default function App() {
  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}
